<template>
  <template v-if="!fromOutside">
    <div class="reactionAndTicker">
      <PostReactions
        :sourceId="sourceId"
        @update-total-count="handleTotalCountUpdate"
        :reactions="reactions"
        :post="post"
        :selectedReaction="currentReactionType"
      />
      <div v-if="processedStockTags.length" class="tickers">
        <PostTickers
          v-for="(stockTag, i) in processedStockTags.slice(0, 5)"
          :key="i"
          :stockTag="stockTag"
        />
        <div v-if="processedStockTags.length > 5" class="stockTagsLength">
          +{{ processedStockTags.length - 5 }}
        </div>
      </div>
    </div>
  </template>
  <div class="foot">
    <div class="leftAlign">
      <!-- Use computed properties from reaction store -->
      <a
        class="icon_footer like"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
        ref="likeIconFooterRef"
      >
        <IconComponent
          noRtlTransform
          path="/post_card"
          v-if="hasUserReacted"
          @click.stop="handleUnlikePost($event)"
          className="post_footer_icon full_width active"
          icon="post_like_active.svg"
        />
        <IconComponent
          noRtlTransform
          path="/post_card"
          v-else
          @click.stop="handleLikePost($event, currentReactionType)"
          className="post_footer_icon full_width"
          icon="icons_like_outline.svg"
        />

        {{ totalReactions }}
      </a>

      <!-- Teleport the popover to avoid modal clipping -->
      <template v-if="!fromOutside">
        <teleport :to="teleportTarget" v-if="showLikeBox">
          <div
            class="likeBox popover_content teleported-popover"
            ref="likeBoxRef"
            @mouseenter="handleMouseEnter"
            @mouseleave="handleMouseLeave"
            :style="popoverStyle"
          >
            <figure
              v-for="reaction in reactions"
              :key="reaction"
              id="pf_like"
              @click.stop="handleIconClicked($event, reaction)"
            >
              <el-tooltip
                :content="reaction ? $t(`common.reactions.${reaction}`) : ''"
                placement="top"
              >
                <img
                  :src="
                    require(`../../../../../assets/images/reactions/${reaction}.png`)
                  "
                />
              </el-tooltip>
            </figure>
          </div>
        </teleport>
      </template>
    </div>
    <div class="rightAlign">
      <a class="icon_footer">
        <IconComponent
          @click.stop="showRepostModal($event)"
          path="/post_card"
          :icon="repostCount > 0 ? `post_share.svg` : `outlined_post_share.svg`"
          noRtlTransform
        />
        {{ repostCount }}
      </a>
      <a class="icon_footer comment">
        <IconComponent
          @click.stop="changeBottomView($event, true)"
          path="/post_card"
          :icon="
            commentCount > 0 ? `post_comment.svg` : `outlined_post_comment.svg`
          "
          noRtlTransform
        />
        {{ commentCount }}
      </a>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  computed,
  watch,
  nextTick,
} from "vue";
import { useReactionStore } from "@/services/reaction.store";
import { useCreatePost } from "../../composables/createpost.composable";
import { RouteLocationRaw } from "vue-router";
import IconComponent from "@/components/IconComponent.vue";
import PostReactions from "./PostCardReactions.vue";
import router from "@/router/router";
import { popModal } from "jenesius-vue-modal";
import { useAuth } from "@/composables/public-auth-signin.composable";
import PostTickers from "./PostCardTickers.vue";
import { UnlikeModel } from "../../models/unlike.model";
import { LikePostModel, ReactionType } from "../../models/likepost.model";
import storageService from "@/services/storage.service";
import { getDirection } from "@/locale/index";
import { useI18n } from "vue-i18n";
import utilService from "@/services/util.service";

// Define a type for valid reaction strings
type ValidReaction =
  | "like"
  | "heart"
  | "appreciated"
  | "kiss"
  | "allgreen"
  | "angrybull"
  | "bloodbath"
  | "crapmine"
  | "crashingdown"
  | "cryptomarket"
  | "deadweight"
  | "diamondhand"
  | "downchart"
  | "feelingsick"
  | "goldmine"
  | "kingofthemarket"
  | "queenofthemarket"
  | "sadbull"
  | "shock"
  | "shocked"
  | "smirkingbull"
  | "tothemood"
  | "upchart";

export default defineComponent({
  props: [
    "sourceId",
    "sourceType",
    "isDetailView",
    "stockTags",
    "post",
    "fromOutside",
    "postingData",
  ],
  components: {
    IconComponent,
    PostTickers,
    PostReactions,
  },
  setup(props, context) {
    const reactionStore = useReactionStore();
    const { repostId } = useCreatePost();
    const { locale } = useI18n();

    // Local state for immediate UI updates
    const localHasUserReacted = ref(false);
    const localTotalReactions = ref(0);
    const localCurrentReactionType = ref<ValidReaction>("like");

    // Reactions list with proper typing
    const reactions = ref<ValidReaction[]>([
      "like",
      "heart",
      "appreciated",
      "kiss",
      "allgreen",
      "angrybull",
      "bloodbath",
      "crapmine",
      "crashingdown",
      "cryptomarket",
      "deadweight",
      "diamondhand",
      "downchart",
      "feelingsick",
      "goldmine",
      "kingofthemarket",
      "queenofthemarket",
      "sadbull",
      "shock",
      "shocked",
      "smirkingbull",
      "tothemood",
      "upchart",
    ]);

    // Optimized computed properties with caching
    const reactionKey = computed(() => `${props.sourceId}-${props.sourceType}`);

    const hasUserReacted = computed(() => {
      return localHasUserReacted.value;
    });

    const totalReactions = computed(() => {
      return localTotalReactions.value;
    });

    const commentCount = computed(() => {
      if (props.post?.commentCount !== undefined) {
        return props.post.commentCount;
      }
      const comment = reactionStore.getCommentCount(
        props.sourceId,
        props.sourceType
      );
      return comment?.commentCount || 0;
    });

    const repostCount = computed(() => {
      if (props.post?.repostCount !== undefined) {
        return props.post.repostCount;
      }
      const repost = reactionStore.getRepostCount(props.sourceId);
      return repost?.count || 0;
    });

    const currentReactionType = computed(() => {
      return localCurrentReactionType.value;
    });

    // Sync with post embedded data; fallback to store for live listener updates
    const syncWithPost = () => {
      if (!props.post) return;

      const key = reactionKey.value;
      const optimisticReaction = reactionStore.optimisticReactions.map.get(key);
      const storeReaction = reactionStore.getReactionCount(
        props.sourceId,
        props.sourceType
      );

      if (optimisticReaction) {
        localHasUserReacted.value = true;
        localCurrentReactionType.value =
          optimisticReaction.reactionType as ValidReaction;
      } else {
        const currentUserId = storageService.getUser()?._id;
        const userReaction = props.post.userReactions?.find(
          (r) => r.userId === currentUserId
        );
        localHasUserReacted.value =
          props.post.userHasReacted ??
          reactionStore.hasUserReacted(props.sourceId, props.sourceType);
        localCurrentReactionType.value =
          (userReaction?.reactionType as ValidReaction) ||
          (reactionStore.getUserReactionType(
            props.sourceId,
            props.sourceType
          ) as ValidReaction) ||
          "like";
      }

      const embeddedTotal: number =
        Object.values(props.post.reactionCounts || {}).reduce<number>(
          (sum, c) => sum + (Number(c) || 0),
          0
        ) || Number(props.post.totalReactions) || 0;

      localTotalReactions.value = Math.max(
        embeddedTotal,
        storeReaction?.count || 0
      );
    };

    watch(
      () => [
        props.post?.totalReactions,
        props.post?.userHasReacted,
        props.post?.userReactions,
        props.post?.reactionCounts,
      ],
      () => {
        syncWithPost();
      },
      { deep: true, immediate: true }
    );

    watch(
      () => reactionStore.reactionCountsVersion,
      () => {
        utilService.throttle(() => {
          syncWithPost();
        }, 500)();
      },
      { flush: "sync" }
    );

    const toReactionType = (reaction: string): ReactionType => {
      const reactionMap: Record<string, ReactionType> = {
        like: ReactionType.Like,
        heart: ReactionType.Heart,
        appreciated: ReactionType.Appreciated,
        kiss: ReactionType.Kiss,
        allgreen: ReactionType.ALLGREEN,
        angrybull: ReactionType.ANGRYBULL,
        bloodbath: ReactionType.BLOODBATH,
        crapmine: ReactionType.CRAPMINE,
        crashingdown: ReactionType.CRASHINGDOWN,
        cryptomarket: ReactionType.CRYPTOMARKET,
        deadweight: ReactionType.DEADWEIGHT,
        diamondhand: ReactionType.DIAMONDHAND,
        downchart: ReactionType.DOWNCHART,
        feelingsick: ReactionType.FEELINGSICK,
        goldmine: ReactionType.GOLDMINE,
        kingofthemarket: ReactionType.KINGOFTHEMARKET,
        queenofthemarket: ReactionType.QUEENOFTHEMARKET,
        sadbull: ReactionType.SANDBULL,
        shock: ReactionType.SHOCK,
        shocked: ReactionType.SHOCKED,
        smirkingbull: ReactionType.SMIRKINGBULL,
        tothemood: ReactionType.TOTHEMOOD,
        upchart: ReactionType.UPCHART,
      };

      return reactionMap[reaction] || ReactionType.Like;
    };

    const processedStockTags = computed(() => {
      const unique = new Map();

      for (const item of props.stockTags) {
        const stockId = item.stockId;
        if (!unique.has(stockId)) {
          unique.set(stockId, {
            ...item,
            symbol: item.symbol ? item.symbol.replace("$", "") : "",
          });
        }
      }
      return Array.from(unique.values());
    });

    const handleTotalCountUpdate = (newCount: number) => {
      // console.log("Total count updated:", newCount);
    };

    let isReacting = false;

    // Immediate optimistic updates for smooth UX
    const handleLikePost = async (
      event: MouseEvent,
      reactionType: ValidReaction
    ) => {
      if (props.fromOutside) {
        openPublicSignInDialog();
      } else {
        if (isReacting) return;
        isReacting = true;
        const button = event.currentTarget as HTMLElement;
        button.classList.add("reacting");

        try {
          const currentUser = storageService.getUser();
          // IMMEDIATE optimistic update on post + local state
          localHasUserReacted.value = true;
          localCurrentReactionType.value = reactionType;
          localTotalReactions.value += 1;
          if (props.post && currentUser?._id) {
            props.post.addUserReaction({
              userId: currentUser._id,
              userName: currentUser.userName,
              profilePicture: currentUser.profilePicture,
              reactionType,
            });
            props.post.userHasReacted = true;
          }

          const likePostData = new LikePostModel();
          likePostData.sourceId = props.sourceId;
          likePostData.sourceType = props.sourceType;
          likePostData.postId = props.sourceId;
          likePostData.reactionType = toReactionType(reactionType);

          await reactionStore.likePost(likePostData);
          // context.emit("like-post", likePostData);
        } catch (error) {
          // Revert optimistic update on error
          localHasUserReacted.value = false;
          localTotalReactions.value = Math.max(
            0,
            localTotalReactions.value - 1
          );
          const currentUser = storageService.getUser();
          if (props.post && currentUser?._id) {
            props.post.removeUserReaction(currentUser._id);
            props.post.updateUserHasReacted();
          }
          console.error("Error liking post:", error);
        } finally {
          setTimeout(() => {
            button.classList.remove("reacting");
            isReacting = false;
          }, 300);
        }
      }
    };

    const handleUnlikePost = async (event: MouseEvent) => {
      if (props.fromOutside) {
        openPublicSignInDialog();
      } else {
        if (isReacting) return;
        isReacting = true;
        const button = event.currentTarget as HTMLElement;
        button.classList.add("reacting");

        try {
          const currentUser = storageService.getUser();
          // IMMEDIATE optimistic update on post + local state
          localHasUserReacted.value = false;
          localTotalReactions.value = Math.max(
            0,
            localTotalReactions.value - 1
          );
          if (props.post && currentUser?._id) {
            props.post.removeUserReaction(currentUser._id);
            props.post.updateUserHasReacted();
          }

          const unlikeData = new UnlikeModel();
          unlikeData.sourceId = props.sourceId;
          unlikeData.sourceType = props.sourceType;

          await reactionStore.unlikePost(unlikeData);
          context.emit("unlike-post", unlikeData);
        } catch (error) {
          // Revert optimistic update on error
          localHasUserReacted.value = true;
          localTotalReactions.value += 1;
          console.error("Error unliking post:", error);
        } finally {
          setTimeout(() => {
            button.classList.remove("reacting");
            isReacting = false;
            showLikeBox.value = false;
          }, 300);
        }
      }
    };

    const handleIconClicked = async (
      event: MouseEvent,
      reaction: ValidReaction
    ) => {
      showLikeBox.value = false;
      await handleLikePost(event, reaction);
    };

    const showRepostModal = (event: any) => {
      if (props.fromOutside) {
        openPublicSignInDialog();
      } else {
        popModal();
        event.stopPropagation();
        repostId.value = props.sourceId;
        if (router.currentRoute.value.name == "postDetail") {
          router.replace({ name: "feed" });
        }
      }
    };

    const changeBottomView = (event: any, isComment: boolean) => {
      if (props.fromOutside) {
        openPublicSignInDialog();
      } else {
        event.stopPropagation();
        if (!props.isDetailView) {
          viewPostDetail();
        }
      }
    };

    const viewPostDetail = () => {
      const route: RouteLocationRaw = {
        name: "postDetail",
        params: { postId: props.sourceId },
      };
      if (router.currentRoute.value.name == "postDetail") {
        router.replace(route);
      } else {
        router.push(route);
      }
    };

    const capitalizeFirstLetter = (str: string) => {
      return str ? str.charAt(0).toUpperCase() + str.slice(1) : "";
    };

    const hideReactions = ref(false);
    const showLikeBox = ref(false);
    const popoverPosition = ref({ x: 0, y: 0 });
    let hideTimer: ReturnType<typeof setTimeout> | null = null;

    const teleportTarget = computed(() => {
      return document.querySelector(".modal--backdrop")
        ? ".modal--backdrop"
        : "body";
    });

    const popoverStyle = computed(() => {
      const styles = {
        position: "fixed" as const,
        top: `${popoverPosition.value.y - 60}px`,
        zIndex: teleportTarget.value === "body" ? 999 : 10000,
        left: `${popoverPosition.value.x}px`,
      };

      if (getDirection(locale.value) == "rtl") {
        styles.left = `${popoverPosition.value.x - 255}px`;
      }

      return styles;
    });

    const updatePopoverPosition = async (event: MouseEvent) => {
      await nextTick();
      const target = event.currentTarget as HTMLElement;
      if (target) {
        const rect = target.getBoundingClientRect();
        popoverPosition.value = {
          x: rect.left,
          y: rect.top - 80,
        };
      }
    };

    const handleMouseEnter = (event: MouseEvent) => {
      if (hideTimer) {
        clearTimeout(hideTimer);
        hideTimer = null;
      }

      const target = event.currentTarget as HTMLElement;
      if (target && target.classList.contains("like")) {
        updatePopoverPosition(event);
      }

      showLikeBox.value = true;
    };

    const handleMouseLeave = (event: MouseEvent) => {
      const relatedTarget = event.relatedTarget as HTMLElement;

      if (
        relatedTarget &&
        (relatedTarget.closest(".el-tooltip__popper") ||
          relatedTarget.closest(".el-popper") ||
          relatedTarget.closest(".likeBox") ||
          relatedTarget.closest(".like"))
      ) {
        return;
      }

      hideTimer = setTimeout(() => {
        showLikeBox.value = false;
        hideTimer = null;
      }, 400);
    };

    const likeIconFooterRef = ref<HTMLElement | null>(null);
    const likeBoxRef = ref<HTMLElement | null>(null);

    const { showPublicSignIn } = useAuth();
    const openPublicSignInDialog = async () => {
      await showPublicSignIn({
        fromHeader: true,
        fromPostDetail: true,
        postData: props.postingData,
      });
    };

    return {
      handleTotalCountUpdate,
      processedStockTags,
      reactions,
      handleLikePost,
      handleUnlikePost,
      handleIconClicked,
      showRepostModal,
      changeBottomView,
      capitalizeFirstLetter,
      showLikeBox,
      handleMouseEnter,
      handleMouseLeave,
      popoverStyle,
      teleportTarget,
      likeIconFooterRef,
      likeBoxRef,
      hideReactions,
      currentReactionType,
      hasUserReacted,
      totalReactions,
      commentCount,
      repostCount,
    };
  },
});
</script>

<style lang="scss" scoped>
.icon_footer {
  transition: all 0.2s ease-in-out;

  .svg_icon {
    margin-inline-end: 8px;
    width: 22px;
    height: 22px;
  }
}

.icon_footer.reacting {
  transform: scale(0.8);
  opacity: 0.7;
}

.likeBox figure img {
  transition: transform 0.2s ease;
}

.likeBox figure:active img {
  transform: scale(1.2);
}

.foot {
  border: 1px solid var(--base-new-border-dark) !important;
  border-inline-end: 0px !important;
  border-inline-start: 0px !important;
}

.reactionAndTicker {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  .tickers {
    display: flex;
    align-items: center;
    gap: 5px;

    .stock_logo img {
      border-radius: 6px;
    }

    @media screen and (min-width: 1280px) and (max-width: 1891px) {
      .stock_logo {
        border-width: 1px !important;

        img {
          border-radius: 4px !important;
        }
      }
    }

    > * {
      margin-inline-start: 0px;
    }

    .stockTagsLength {
      width: 28px;
      flex: 0 0 28px;
      height: 28px;
      display: flex;
      justify-content: center;
      align-items: center;
      background: var(--base-light);
      border-radius: 6px;
      color: var(--base-text);
      font-size: var(--font-2xs);
    }
  }
}
</style>
